import React, { useContext, useEffect, useState, useRef } from "react";

import { observer } from "mobx-react";
import { useSearchParams, useParams } from "../../../hooks/useNavigation";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "styled-components";

import { useAppState } from "../../../../contexts/ApplicationStateProvider";
import ComponentContext from "../../../../contexts/ComponentContext";
import { useStores } from "../../../../contexts/StoreContext";
import { SERVER_SIDE_FETCH_LIMIT } from "../../../../stores/FiltersStore";
import { useAnalytics } from "../../../helpers/AnalyticsTrackingContext";
import Loader from "../../Loader/Loader";
import NoVacancies from "../NoVacancies";
import VacancyModal from "../VacancyModal";
import VacancyListItem from "./VacancyItem";

const EndMessage = styled.p`
  color: ${({ $endOfResultsTextColour }) => $endOfResultsTextColour || "inherit"};
`;

function VacancyList() {
  const { thumbnail, endOfResultsTextColour, endOfResultsClassName } =
    useContext(ComponentContext);
  const { sendTrackingEvent } = useAnalytics();
  const [currentPage, setCurrentPage] = useState(1);
  const { modalStore, filtersStore, vacancyStore } = useStores();
  const pageStartTime = useRef(null);
  const { openModal } = modalStore;
  const {
    searchResults,
    pagination,
    numberOfPages,
    isFetching,
  } = filtersStore;
  const {
    setSelectedVacancy,
    setIsVacancyLoading,
    selectedVacancy,
    fetchSingleVacancy,
  } = vacancyStore;
  const searchParams = useSearchParams();
  const { isMobile } = useAppState();

  const thumbnailUrl =
    thumbnail?.formats?.thumbnail?.url || thumbnail?.url || null;

  const handleClick = (vacancy, e) => {
    e.preventDefault();
    fetchSingleVacancy(vacancy);
    if (isMobile) {
      openModal({
        content: <VacancyModal thumbnailUrl={thumbnailUrl} />,
        fullscreen: true,
      });
    }
  };

  const fetchMoreData = async () => {
    await filtersStore.search({
      append: true,
      start: (currentPage - 1) * pagination.perPage + SERVER_SIDE_FETCH_LIMIT,
      limit: pagination.perPage,
    });

    if (currentPage < numberOfPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const hasMounted = useRef(false);

  const searchParamsString = searchParams.toString();
  const frontendFiltersString = JSON.stringify(filtersStore.frontendFilters);

  useEffect(() => {
    filtersStore.search().then(({ vacancies }) => {
      hasMounted.current = true;
      setSelectedVacancy(vacancies?.[0]);
      setIsVacancyLoading(false);
      setCurrentPage(1);
    });
  }, [searchParamsString, frontendFiltersString]);

  useEffect(() => {
    if (selectedVacancy?.id) {
      sendTrackingEvent("SEARCH_RESULT_VIEW", selectedVacancy.id);
      pageStartTime.current = Date.now();
    }
    return () => {
      pageStartTime.current = null;
    };
  }, [selectedVacancy?.id]);

  if (!isFetching && searchResults.length === 0) {
    return <NoVacancies />;
  }

  return (
    <div>
      <InfiniteScroll
        dataLength={searchResults.length}
        endMessage={
          <EndMessage
            className={`text-center mt-4 fw-bold ${endOfResultsClassName}`}
            $endOfResultsTextColour={endOfResultsTextColour}
          >
            End of results
          </EndMessage>
        }
        hasMore={currentPage !== numberOfPages}
        loader={<Loader />}
        next={fetchMoreData}
        scrollableTarget="infinite-scroll-default-search-results"
        scrollThreshold={0.85}
      >
        {searchResults?.map((vacancy) => (
          <VacancyListItem
            key={vacancy.vacancyId}
            isSelected={selectedVacancy?.id === vacancy.id}
            onClick={(_, e) => {
              if (selectedVacancy?.id !== vacancy.id) {
                const timeOnPage = pageStartTime.current
                  ? Date.now() - pageStartTime.current
                  : 0;
                sendTrackingEvent("SEARCH_RESULT_EXIT", vacancy.id, {
                  timeOnPage,
                });
                pageStartTime.current = null;

                handleClick(vacancy, e);
              }
            }}
            thumbnailUrl={vacancy?.company?.logoUrlFull || thumbnailUrl}
            vacancy={vacancy}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
}

export default observer(VacancyList);
